import { createContext, useContext, useState, useCallback, useEffect, useMemo, type ReactNode } from "react";

// ── Data types ──────────────────────────────────────────────────────────

export interface LibraryItem {
  id: string;
  label: string;
  color?: string;
  disabled?: boolean;
  /** Opaque bag — semantic summaries, tag counts, notification settings, raw data refs, etc. */
  metadata?: Record<string, unknown>;
}

export interface LibraryCategory {
  id: string;
  label: string;
  itemIds: string[];
}

export interface LibraryGroup {
  id: string;
  label: string;
  categories?: LibraryCategory[];
  items: LibraryItem[];
  createAction?: { label: string; onClick: () => void };
}

export interface LibrarySection {
  id: string;
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
  groups: LibraryGroup[];
  /** "reference" sections render with a visual separator and muted styling (e.g., Newsquawk Library) */
  sectionVariant?: "default" | "reference";
}

// ── Context value ───────────────────────────────────────────────────────

export type LibraryBrowserMode = "filter" | "browse" | "builder";

export interface LibraryBrowserContextValue {
  // Navigation
  activePath: string[];
  navigateTo: (path: string[]) => void;
  navigateUp: () => void;

  // Selected item (for detail pane)
  selectedItem: LibraryItem | null;
  selectedItemSectionId: string | null;
  selectItem: (item: LibraryItem | null, sectionId?: string) => void;

  // Search
  searchQuery: string;
  setSearchQuery: (q: string) => void;

  // Mode
  mode: LibraryBrowserMode;
  /** "filter" or "highlight" — controls color display behavior */
  variant?: "filter" | "highlight";

  // Filter mode state
  selections: Record<string, string[]>;
  onToggle: (menuId: string, filterId: string) => void;
  onClearSelections: () => void;
  selectionsCount: number;

  // Callbacks
  onPreview?: (item: LibraryItem, sectionId: string) => void;
  onItemEdit?: (item: LibraryItem, sectionId: string) => void;
  onCreate?: (sectionId: string) => void;

  // Data
  sections: LibrarySection[];

  /**
   * Opt in to searching the whole tag library from the list's search box.
   *
   * `sectionId` is the section a tag picked from the library is toggled
   * against, since `onToggle` is keyed by section. Hosts that leave this unset
   * search only the sections they were given, exactly as before.
   */
  tagLibrary?: { sectionId: string };
}

const LibraryBrowserContext = createContext<LibraryBrowserContextValue | null>(null);

// ── Hook ────────────────────────────────────────────────────────────────

export function useLibraryBrowser() {
  const ctx = useContext(LibraryBrowserContext);
  if (!ctx) throw new Error("useLibraryBrowser must be used within a LibraryBrowserProvider");
  return ctx;
}

// ── Provider ────────────────────────────────────────────────────────────

export interface LibraryBrowserProviderProps {
  mode: LibraryBrowserMode;
  variant?: "filter" | "highlight";
  sections: LibrarySection[];
  children?: ReactNode;

  // Filter/builder mode
  selections?: Record<string, string[]>;
  onSelectionsChange?: (selections: Record<string, string[]>) => void;
  /** Override the default toggle handler (used by builder mode for custom logic) */
  onToggleOverride?: (menuId: string, filterId: string) => void;

  // Callbacks
  onPreview?: (item: LibraryItem, sectionId: string) => void;
  onItemEdit?: (item: LibraryItem, sectionId: string) => void;
  onCreate?: (sectionId: string) => void;

  /** Initial active path — auto-navigates on mount (e.g., ["nq-library"] to show Newsquawk Library) */
  defaultActivePath?: string[];

  /** Search the whole tag library too; see the context value's field. */
  tagLibrary?: { sectionId: string };
}

export function LibraryBrowserProvider({
  mode,
  sections,
  children,
  selections: controlledSelections,
  onSelectionsChange,
  onPreview,
  onItemEdit,
  onCreate,
  onToggleOverride,
  tagLibrary,
  variant,
  defaultActivePath,
}: LibraryBrowserProviderProps) {
  // Navigation
  const [activePath, setActivePath] = useState<string[]>(defaultActivePath ?? []);
  const [selectedItem, setSelectedItem] = useState<LibraryItem | null>(null);
  const [selectedItemSectionId, setSelectedItemSectionId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  // Internal selections state (for uncontrolled usage)
  const [internalSelections, setInternalSelections] = useState<Record<string, string[]>>({});
  const selections = controlledSelections ?? internalSelections;

  const navigateTo = useCallback((path: string[]) => {
    setActivePath(path);
    setSelectedItem(null);
    setSelectedItemSectionId(null);
  }, []);

  const navigateUp = useCallback(() => {
    setActivePath((prev) => prev.slice(0, -1));
    setSelectedItem(null);
    setSelectedItemSectionId(null);
  }, []);

  // Re-sync selectedItem when sections update (e.g., after color/priority override changes)
  useEffect(() => {
    if (!selectedItem) return;
    for (const section of sections) {
      for (const group of section.groups) {
        const updated: LibraryItem | undefined = group.items.find((i) => i.id === selectedItem.id);
        if (updated && updated !== selectedItem) {
          setSelectedItem(updated);
          return;
        }
      }
    }
  }, [sections]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectItem = useCallback((item: LibraryItem | null, sectionId?: string) => {
    setSelectedItem((prev) => {
      // Toggle off if clicking same item
      if (prev && item && prev.id === item.id) return null;
      return item;
    });
    setSelectedItemSectionId(() => {
      if (selectedItem && item && selectedItem.id === item.id) return null;
      return sectionId ?? null;
    });
  }, [selectedItem]);

  const defaultToggle = useCallback((menuId: string, filterId: string) => {
    // Map flattened section IDs back to the original config ID
    // e.g., "ticker-watchlists-filter--user-tickers" → "ticker-watchlists-filter"
    const selectionKey = menuId.includes("--") ? menuId.split("--")[0] : menuId;
    const next = { ...selections };
    const current = next[selectionKey] || [];
    const isSelected = current.includes(filterId);
    next[selectionKey] = isSelected
      ? current.filter((id) => id !== filterId)
      : [...current, filterId];

    if (onSelectionsChange) {
      onSelectionsChange(next);
    } else {
      setInternalSelections(next);
    }
  }, [selections, onSelectionsChange]);

  const onToggle = onToggleOverride ?? defaultToggle;

  const onClearSelections = useCallback(() => {
    if (onSelectionsChange) {
      onSelectionsChange({});
    } else {
      setInternalSelections({});
    }
  }, [onSelectionsChange]);

  const selectionsCount = useMemo(
    () => Object.values(selections).reduce((sum, ids) => sum + ids.length, 0),
    [selections]
  );

  const value = useMemo<LibraryBrowserContextValue>(
    () => ({
      activePath,
      navigateTo,
      navigateUp,
      selectedItem,
      selectedItemSectionId,
      selectItem,
      searchQuery,
      setSearchQuery,
      mode,
      variant,
      selections,
      onToggle,
      onClearSelections,
      selectionsCount,
      onPreview,
      onItemEdit,
      onCreate,
      sections,
      tagLibrary,
    }),
    [
      activePath,
      navigateTo,
      navigateUp,
      selectedItem,
      selectedItemSectionId,
      selectItem,
      searchQuery,
      mode,
      variant,
      selections,
      onToggle,
      defaultToggle,
      onToggleOverride,
      onClearSelections,
      selectionsCount,
      onPreview,
      onItemEdit,
      onCreate,
      sections,
      tagLibrary,
    ]
  );

  return (
    <LibraryBrowserContext.Provider value={value}>
      {children}
    </LibraryBrowserContext.Provider>
  );
}
